<script setup>
import {
    ref,
    getCurrentInstance
} from 'vue'
import {
    DriverStore
} from '../pinia/Driver.js'

const Driver = DriverStore()
const {
    proxy
} = getCurrentInstance()
const snackbar = ref(false)
const error = ref('')

async function PrestigeUp() {
    error.value = ''
    try {
        let result = await proxy.$http.post(
            "",
            JSON.stringify({
                serviceName: "account",
                methodName: "PrestigeUp",
                parameters: []
            })
        )
        if (result.data) {
            // Reload driver data to get updated prestige/level
            snackbar.value = true
        }
    } catch (err) {
        error.value = err.response?.data?.error || 'Error upgrading prestige'
        console.error("Prestige error:", err)
    }
}
</script>

<template>
<div class="prestigeForm">
    <v-snackbar :timeout="2000" color="deep-purple-accent-4" elevation="24" v-model="snackbar">
        Congratulations! You've reached a new prestige level!
    </v-snackbar>

    <v-alert class="error" v-if="error" :text="error" type="error"></v-alert>

    <v-form>
        <v-container>
            <!-- Prestige Status Card -->
            <div class="settings-card">
                <div class="settings-card-header">
                    <div class="header-icon">
                        <v-icon size="28">mdi-shield-star</v-icon>
                    </div>
                    <div class="header-content">
                        <h3 class="settings-title">Prestige System</h3>
                        <p class="settings-subtitle">Reach level 100 to increase your prestige</p>
                    </div>
                </div>

                <div class="settings-card-body">
                    <!-- Current Prestige -->
                    <div class="setting-item">
                        <div class="setting-info full-width">
                            <div class="setting-label">
                                <v-icon size="20" class="mr-2">mdi-star-circle</v-icon>
                                Current Prestige
                            </div>
                            <div class="prestige-display">
                                <div class="prestige-value">{{ Driver.persona.prestige }}</div>
                                <div class="prestige-max">/ 5</div>
                            </div>
                        </div>
                    </div>

                    <v-divider class="my-4"></v-divider>

                    <!-- Progress Info -->
                    <div class="setting-item">
                        <div class="setting-info full-width">
                            <div class="setting-label">
                                <v-icon size="20" class="mr-2">mdi-chart-line</v-icon>
                                Progress
                            </div>
                            <div class="setting-description">
                                {{ Driver.persona.prestige >= 5 ? 'Maximum prestige reached!' : `Next prestige level in ${100 - Driver.persona.level} level${(100 - Driver.persona.level) > 1 ? 's' : ''}` }}
                            </div>
                            
                            <v-progress-linear
                                :model-value="Driver.persona.level"
                                :max="100"
                                height="12"
                                rounded
                                color="primary"
                                class="progress-bar"
                            >
                                <template v-slot:default>
                                    <strong>Level {{ Driver.persona.level }} / 100</strong>
                                </template>
                            </v-progress-linear>
                        </div>
                    </div>

                    <v-divider class="my-4"></v-divider>

                    <!-- Prestige Action -->
                    <div class="setting-item">
                        <div class="setting-info full-width">
                            <div v-if="Driver.persona.prestige >= 5" class="max-prestige-notice">
                                <v-icon size="32" color="primary">mdi-trophy</v-icon>
                                <div class="notice-text">
                                    <div class="notice-title">Maximum Prestige Achieved!</div>
                                    <div class="notice-subtitle">You've reached the highest prestige level (5)</div>
                                </div>
                            </div>
                            
                            <div v-else-if="Driver.persona.level != 100" class="prestige-requirement">
                                <v-icon size="24" color="warning">mdi-information</v-icon>
                                <div class="requirement-text">
                                    Reach level 100 to unlock prestige upgrade
                                </div>
                            </div>

                            <v-btn
                                v-else
                                variant="elevated"
                                color="primary"
                                size="large"
                                block
                                @click="PrestigeUp"
                                class="prestige-btn"
                            >
                                <v-icon class="mr-2">mdi-arrow-up-bold-circle</v-icon>
                                PRESTIGE UP
                            </v-btn>
                        </div>
                    </div>
                </div>
            </div>
        </v-container>
    </v-form>
</div>
</template>

<style scoped>
.prestigeForm {
    margin: 0;
    padding: 0;
}

.prestigeForm .v-icon {
    color: currentColor;
    opacity: 1;
}

.settings-card {
    background: rgba(255, 255, 255, 0.03);
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    overflow: hidden;
}

.settings-card-header {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;
    background: rgba(var(--v-theme-primary), 0.1);
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.header-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background: rgba(var(--v-theme-primary), 0.2);
    border-radius: 10px;
}

.header-icon .v-icon {
    color: rgb(var(--v-theme-primary));
}

.header-content {
    flex: 1;
}

.settings-title {
    margin: 0;
    font-size: 1.3em;
    font-weight: 600;
    color: rgb(var(--v-theme-primary));
}

.settings-subtitle {
    margin: 4px 0 0 0;
    font-size: 0.85em;
    color: rgba(255, 255, 255, 0.6);
}

.settings-card-body {
    padding: 24px;
}

.setting-item {
    display: flex;
    align-items: flex-start;
    gap: 16px;
}

.setting-info {
    flex: 1;
}

.setting-info.full-width {
    width: 100%;
}

.setting-label {
    display: flex;
    align-items: center;
    font-size: 1em;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 16px;
}

.setting-label .v-icon {
    color: rgba(255, 255, 255, 0.9);
}

.setting-description {
    font-size: 0.95em;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 16px;
    font-weight: 500;
}

.prestige-display {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 8px;
    padding: 16px;
    background: rgba(var(--v-theme-primary), 0.15);
    border-radius: 12px;
    border: 2px solid rgba(var(--v-theme-primary), 0.3);
}

.prestige-value {
    font-size: 3.5em;
    font-weight: 700;
    color: rgb(var(--v-theme-primary));
    line-height: 1;
}

.prestige-max {
    font-size: 1.8em;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.5);
}

.progress-bar {
    margin-top: 8px;
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.progress-bar strong {
    font-size: 0.75em;
    color: white;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
}

.max-prestige-notice {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;
    background: rgba(var(--v-theme-primary), 0.1);
    border-radius: 12px;
    border: 2px solid rgba(var(--v-theme-primary), 0.3);
}

.notice-text {
    flex: 1;
}

.notice-title {
    font-size: 1.1em;
    font-weight: 600;
    color: rgb(var(--v-theme-primary));
    margin-bottom: 4px;
}

.notice-subtitle {
    font-size: 0.9em;
    color: rgba(255, 255, 255, 0.6);
}

.prestige-requirement {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    background: rgba(255, 193, 7, 0.1);
    border-radius: 12px;
    border: 1px solid rgba(255, 193, 7, 0.3);
}

.requirement-text {
    font-size: 0.95em;
    color: rgba(255, 193, 7, 0.9);
    font-weight: 500;
}

.prestige-btn {
    margin-top: 8px;
    font-weight: 600;
    letter-spacing: 0.5px;
}

.prestige-btn .v-icon {
    opacity: 1 !important;
}

.error {
    margin-bottom: 20px;
}
</style>

<style>
.v-input__details {
    padding-inline-start: 2px !important;
}
</style>
